import * as React from 'react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis, LabelList } from 'recharts'
import { cn } from '@/lib/utils'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart'

/**
 * Figma (node 520:6888 — Line Chart):
 *
 * 7 variants:
 *   Basic        — Natural curve, single series, stroke only
 *   Linear       — Linear interpolation
 *   Step         — Step interpolation
 *   Multiple     — Two line series
 *   Dots         — Line with dot markers
 *   Dots Colors  — Dots with per-point colors
 *   Label        — Line with value labels
 *
 * Specs:
 *   Line: stroke 2px, no fill
 *   Dots: size 6, stroke-width 2
 *   Colors: --chart-1 through --chart-5 via CSS variables
 *   Grid: horizontal dashed lines
 *   Axis: text-xs text-muted-foreground, tick line hidden
 */

export type LineChartCurveType = 'natural' | 'linear' | 'step'

export interface IntuiLineChartProps {
  /** Chart data array */
  data: Record<string, unknown>[]
  /** Chart configuration (keys, labels, colors) */
  config: ChartConfig
  /** Data keys to render as lines (defaults to all config keys) */
  dataKeys?: string[]
  /** X axis data key */
  xAxisKey?: string
  /** Curve interpolation type */
  curveType?: LineChartCurveType
  /** Show dots on data points */
  showDots?: boolean
  /** Dot radius */
  dotRadius?: number
  /** Show value labels on data points */
  showLabels?: boolean
  /** Show chart legend */
  showLegend?: boolean
  /** Show X axis */
  showXAxis?: boolean
  /** X tick label formatter. Default keeps the first three characters of a string (month names). */
  xTickFormatter?: (value: unknown) => string
  /** Minimum pixel gap between X ticks; recharts hides ticks that would be closer. */
  xMinTickGap?: number
  /** Show Y axis */
  showYAxis?: boolean
  /** Show cartesian grid */
  showGrid?: boolean
  /** Show tooltip on hover */
  showTooltip?: boolean
  /** Stroke width */
  strokeWidth?: number
  /** Custom dot renderer (for colored dots variant) */
  customDot?: React.ComponentProps<typeof Line>['dot']
  /** Additional className for the container */
  className?: string
}

function IntuiLineChart({
  data,
  config,
  dataKeys,
  xAxisKey = 'month',
  curveType = 'natural',
  showDots = false,
  dotRadius = 4,
  showLabels = false,
  showLegend = false,
  showXAxis = true,
  xTickFormatter,
  xMinTickGap,
  showYAxis = false,
  showGrid = true,
  showTooltip = true,
  strokeWidth = 2,
  customDot,
  className,
}: IntuiLineChartProps) {
  const keys = dataKeys ?? Object.keys(config)

  return (
    <ChartContainer config={config} className={cn(className)}>
      <LineChart
        data={data}
        margin={{ left: showYAxis ? 12 : 0, right: 12 }}
      >
        {showGrid && <CartesianGrid vertical={false} />}
        {showXAxis && (
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={xMinTickGap}
            tickFormatter={
              xTickFormatter ??
              ((value) => (typeof value === 'string' ? value.slice(0, 3) : value))
            }
          />
        )}
        {showYAxis && (
          <YAxis tickLine={false} axisLine={false} tickMargin={8} />
        )}
        {showTooltip && (
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel={keys.length === 1} />}
          />
        )}
        {keys.map((key) => (
          <Line
            key={key}
            dataKey={key}
            type={curveType}
            stroke={`var(--color-${key})`}
            strokeWidth={strokeWidth}
            dot={customDot ?? (showDots ? { r: dotRadius, strokeWidth: 2 } : false)}
          >
            {showLabels && (
              <LabelList
                position="top"
                offset={12}
                className="fill-foreground"
                fontSize={12}
              />
            )}
          </Line>
        ))}
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}
      </LineChart>
    </ChartContainer>
  )
}
IntuiLineChart.displayName = 'IntuiLineChart'

export { IntuiLineChart }
export type { ChartConfig }
